import * as React from "react"
import { motion } from "motion/react"
import { Mic, Calendar, Clock, Users, MessageCircle, Send, Loader2, ExternalLink, RefreshCw, Radio, CalendarDays, Zap, Trophy } from "lucide-react"
import { useAuth, type AmaEvent, type GameEventWithProgress } from "@/contexts/AuthContext"
import { getEventIcon } from "@/lib/iconMap"
import { GameEventDetailModal } from "./GameEventDetailModal"

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring" as const, bounce: 0.3, duration: 0.45 },
  },
}

// Mock event types for the placeholder UI
type EventType = "ama" | "tournament" | "flash" | "training"

interface MockEvent {
  id: string
  name: string
  type: EventType
  dateTime: string
  status: "live" | "upcoming" | "ended"
  description?: string
}

// Placeholder mock events (will be replaced with real API data later)
const mockLiveEvents: MockEvent[] = []
type AmaEmbedConfig = {
  provider: "youtube" | "twitch"
  src: string
  title: string
}

function parseYoutubeVideoId(url: URL): string | null {
  const host = url.hostname.toLowerCase()

  if (host === "youtu.be") {
    const id = url.pathname.split("/").filter(Boolean)[0] || ""
    return id || null
  }

  if (host === "youtube.com" || host.endsWith(".youtube.com")) {
    if (url.pathname === "/watch") {
      return url.searchParams.get("v") || null
    }

    const parts = url.pathname.split("/").filter(Boolean)
    if (parts[0] === "embed" && parts[1]) return parts[1]
    if (parts[0] === "shorts" && parts[1]) return parts[1]
    if (parts[0] === "live" && parts[1]) return parts[1]
  }

  return null
}

function buildAmaEmbedConfig(streamUrl: string | null | undefined, twitchParentHost: string | null): AmaEmbedConfig | null {
  if (!streamUrl) return null

  try {
    const url = new URL(streamUrl)
    const host = url.hostname.toLowerCase()

    if (host === "youtu.be" || host === "youtube.com" || host.endsWith(".youtube.com")) {
      const videoId = parseYoutubeVideoId(url)
      if (!videoId) return null
      return {
        provider: "youtube",
        src: `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?rel=0`,
        title: "YouTube event stream",
      }
    }

    if (host === "twitch.tv" || host.endsWith(".twitch.tv")) {
      if (!twitchParentHost) return null

      const parts = url.pathname.split("/").filter(Boolean)
      const parent = encodeURIComponent(twitchParentHost)

      if (host === "clips.twitch.tv") {
        const clip = parts[0] || url.searchParams.get("clip") || ""
        if (!clip) return null
        return {
          provider: "twitch",
          src: `https://clips.twitch.tv/embed?clip=${encodeURIComponent(clip)}&parent=${parent}`,
          title: "Twitch event clip",
        }
      }

      if (host === "player.twitch.tv") {
        const channel = url.searchParams.get("channel")
        const video = url.searchParams.get("video")
        if (channel) {
          return {
            provider: "twitch",
            src: `https://player.twitch.tv/?channel=${encodeURIComponent(channel)}&parent=${parent}&autoplay=false`,
            title: "Twitch event stream",
          }
        }
        if (video) {
          return {
            provider: "twitch",
            src: `https://player.twitch.tv/?video=${encodeURIComponent(video)}&parent=${parent}&autoplay=false`,
            title: "Twitch event video",
          }
        }
        return null
      }

      if (parts[0] === "videos" && parts[1]) {
        return {
          provider: "twitch",
          src: `https://player.twitch.tv/?video=v${encodeURIComponent(parts[1])}&parent=${parent}&autoplay=false`,
          title: "Twitch event video",
        }
      }

      if (parts[0] === "clip" && parts[1]) {
        return {
          provider: "twitch",
          src: `https://clips.twitch.tv/embed?clip=${encodeURIComponent(parts[1])}&parent=${parent}`,
          title: "Twitch event clip",
        }
      }

      const channel = parts[0]
      if (!channel) return null
      return {
        provider: "twitch",
        src: `https://player.twitch.tv/?channel=${encodeURIComponent(channel)}&parent=${parent}&autoplay=false`,
        title: "Twitch event stream",
      }
    }
  } catch {
    return null
  }

  return null
}

export function EventsTab() {
  const { getAmaEvents, getGameEvents } = useAuth()
  const [events, setEvents] = React.useState<AmaEvent[]>([])
  const [gameEvents, setGameEvents] = React.useState<GameEventWithProgress[]>([])
  const [featuredGameEvent, setFeaturedGameEvent] = React.useState<GameEventWithProgress | null>(null)
  const [selectedEventId, setSelectedEventId] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [refreshing, setRefreshing] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [question, setQuestion] = React.useState("")
  const [questionFeedback, setQuestionFeedback] = React.useState<string | null>(null)
  const [twitchParentHost, setTwitchParentHost] = React.useState<string | null>(null)

  const loadEvents = React.useCallback(async (silent?: boolean) => {
    if (!silent) setLoading(true)
    setError(null)

    const [amaResult, gameResult] = await Promise.all([
      getAmaEvents(),
      getGameEvents(),
    ])

    if (amaResult.success && amaResult.events) {
      setEvents(amaResult.events)
    } else {
      setError(amaResult.message || "Failed to load events")
    }

    if (gameResult.success) {
      setGameEvents(gameResult.events || [])
      setFeaturedGameEvent(gameResult.featuredEvent || null)
    }

    if (!silent) setLoading(false)
  }, [getAmaEvents, getGameEvents])

  React.useEffect(() => {
    loadEvents()
  }, [loadEvents])

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      setTwitchParentHost(window.location.hostname || "localhost")
    }
  }, [])

  const handleRefresh = async () => {
    setRefreshing(true)
    await loadEvents(true)
    setRefreshing(false)
  }

  const handleQuestionSubmit = () => {
    if (!question.trim()) return
    setQuestion("")
    setQuestionFeedback("Question received. We'll surface the most upvoted prompts next.")
    setTimeout(() => setQuestionFeedback(null), 2500)
  }

  const isAmaLiveNow = (ama: AmaEvent) => {
    const startsMs = new Date(ama.startsAt).getTime()
    if (!Number.isFinite(startsMs)) return false
    const durationMs = Math.max(1, ama.durationMinutes || 0) * 60 * 1000
    const endsMs = startsMs + durationMs
    const nowMs = Date.now()
    return nowMs >= startsMs && nowMs <= endsMs
  }

  const liveAmaEvents = events.filter(isAmaLiveNow)
  const nonLiveAmaEvents = events.filter((ama) => !isAmaLiveNow(ama))
  const hasLiveEvents = mockLiveEvents.length > 0 || liveAmaEvents.length > 0

  const formatWhen = (startsAt: string) =>
    new Date(startsAt).toLocaleString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    })

  const getEventTypeIcon = (type: EventType) => {
    switch (type) {
      case "ama": return <Mic className="w-4 h-4" />
      case "tournament": return <Trophy className="w-4 h-4" />
      case "flash": return <Zap className="w-4 h-4" />
      case "training": return <Users className="w-4 h-4" />
    }
  }

  const getEventTypeLabel = (type: EventType) => {
    switch (type) {
      case "ama": return "Event"
      case "tournament": return "Tournament"
      case "flash": return "Flash Event"
      case "training": return "Training"
    }
  }

  return (
    <motion.div
      className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Page Header - Events */}
      <motion.div className="mb-8" variants={itemVariants}>
        <div className="flex items-center gap-3 mb-3">
          <motion.div
            className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F37020] to-[#FF8C42] border-3 border-[#2A2A45] flex items-center justify-center shadow-[0_0_10px_rgba(243,112,32,0.3)]"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <CalendarDays className="w-6 h-6 text-white" />
          </motion.div>
          <div>
            <h1 className="text-3xl md:text-4xl font-black text-white">Events</h1>
            <p className="text-[#9A9ABF] font-medium">
              Live events and upcoming sessions with top performers.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Live Events Section */}
      {hasLiveEvents && (
        <motion.div
          className="bg-[#1C1C32] border-4 border-[#2A2A45] rounded-2xl p-5 md:p-6 shadow-[0_0_15px_rgba(243,112,32,0.1)] mb-6"
          variants={itemVariants}
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="relative">
              <Radio className="w-5 h-5 text-red-500" />
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
            </div>
            <h2 className="text-xl font-black text-white">Live Events</h2>
            <span className="ml-2 px-2 py-0.5 bg-red-500/20 text-red-400 text-xs font-bold rounded-full border border-red-500/30">
              LIVE
            </span>
          </div>

          <div className="space-y-3">
            {mockLiveEvents.map((event) => (
              <div
                key={event.id}
                className="p-4 rounded-xl bg-gradient-to-r from-red-500/10 to-orange-500/10 border-2 border-red-500/30"
              >
                <div className="flex items-center gap-2 mb-2">
                  {getEventTypeIcon(event.type)}
                  <span className="text-xs font-bold text-red-400 uppercase">{getEventTypeLabel(event.type)}</span>
                  <span className="ml-auto flex items-center gap-1.5 text-xs font-bold text-red-400">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    LIVE NOW
                  </span>
                </div>
                <p className="font-black text-white">{event.name}</p>
                {event.description && <p className="mt-1 text-sm text-[#9A9ABF]">{event.description}</p>}
              </div>
            ))}

            {liveAmaEvents.map((ama) => {
              const embed = buildAmaEmbedConfig(ama.streamUrl, twitchParentHost)
              return (
                <div
                  key={`live-ama-${ama._id}`}
                  className="p-4 rounded-xl bg-gradient-to-r from-red-500/10 to-orange-500/10 border-2 border-red-500/30"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-red-400 uppercase">
                          <Mic className="w-3.5 h-3.5" />
                          EVENT
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-red-400">
                          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                          LIVE NOW
                        </span>
                      </div>
                      <p className="font-black text-white">{ama.title}</p>
                    </div>
                    <a
                      href={ama.joinUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500 text-white border-2 border-[#2A2A45] text-xs font-black whitespace-nowrap hover:translate-y-[-1px] transition-transform"
                    >
                      Join
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>

                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#9A9ABF]">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="w-4 h-4" />
                      {formatWhen(ama.startsAt)}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Users className="w-4 h-4" />
                      {ama.hostName}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="w-4 h-4" />
                      {ama.durationMinutes} min
                    </span>
                  </div>

                  {ama.description && (
                    <p className="mt-2 text-sm text-[#CCCCCC]">{ama.description}</p>
                  )}

                  {embed && (
                    <div className="mt-3 overflow-hidden rounded-xl border-2 border-[#2A2A45] bg-[#11111D]">
                      <div className="aspect-video">
                        <iframe
                          src={embed.src}
                          title={`${ama.title} ${embed.title}`}
                          className="w-full h-full"
                          loading="lazy"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                          allowFullScreen
                        />
                      </div>
                      <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-[#9A9ABF] border-t border-[#2A2A45]">
                        Embedded {embed.provider === "youtube" ? "YouTube" : "Twitch"} stream
                      </div>
                    </div>
                  )}

                  <div className="mt-2 text-[11px] text-[#6A6A8A] font-semibold uppercase tracking-wide">
                    {ama.sendToAll
                      ? "Audience: All agencies"
                      : `Audience: ${ama.agencyName || ama.agencyId || "Targeted agency"}`}
                  </div>
                </div>
              )
            })}
          </div>
        </motion.div>
      )}

      {/* Game Events Section */}
      <motion.div
        className="bg-[#1C1C32] border-4 border-[#2A2A45] rounded-2xl p-5 md:p-6 shadow-[0_0_10px_rgba(243,112,32,0.05)] mb-6"
        variants={itemVariants}
      >
        <div className="flex items-center gap-2 mb-4">
          <CalendarDays className="w-5 h-5 text-[#F37020]" />
          <h2 className="text-xl font-black text-white">Events</h2>
        </div>

        {/* Featured Event Banner */}
        {featuredGameEvent && (
          <div
            className="relative rounded-xl overflow-hidden mb-4 cursor-pointer group"
            onClick={() => setSelectedEventId(featuredGameEvent._id)}
          >
            {featuredGameEvent.imageUrl ? (
              <img
                src={featuredGameEvent.imageUrl}
                alt={featuredGameEvent.title}
                className="w-full aspect-[2/1] object-cover group-hover:scale-[1.02] transition-transform duration-300"
              />
            ) : (
              <div className="w-full aspect-[2/1] bg-gradient-to-br from-[#2A1A40] to-[#1A1A30] flex items-center justify-center">
                <Trophy className="w-12 h-12 text-[#FFD700]/20" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-4">
              <div className="flex items-center gap-2 mb-1">
                {featuredGameEvent.status === "coming_soon" ? (
                  <span className="text-[10px] font-black uppercase tracking-wider bg-[#FFD700] text-black px-2 py-0.5 rounded-md">Coming Soon</span>
                ) : featuredGameEvent.status === "live" ? (
                  <span className="text-[10px] font-black uppercase tracking-wider bg-red-500 text-white px-2 py-0.5 rounded-md flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> LIVE
                  </span>
                ) : null}
                <span className="text-[9px] font-bold uppercase text-white px-1.5 py-0.5 rounded-md" style={{ backgroundColor: featuredGameEvent.badgeColor }}>
                  {featuredGameEvent.badge}
                </span>
              </div>
              <p className="text-white font-black text-lg">{featuredGameEvent.title}</p>
              {featuredGameEvent.description && (
                <p className="text-white/70 text-sm mt-0.5">{featuredGameEvent.description.slice(0, 100)}{featuredGameEvent.description.length > 100 ? "..." : ""}</p>
              )}
            </div>
          </div>
        )}

        {/* Event List */}
        {gameEvents.length === 0 && !featuredGameEvent ? (
          <div className="py-8 text-center">
            <div className="w-14 h-14 rounded-full bg-[#222240] border-2 border-[#2A2A45] flex items-center justify-center mx-auto mb-3">
              <CalendarDays className="w-6 h-6 text-[#6A6A8A]" />
            </div>
            <p className="text-[#6A6A8A] text-sm font-medium">No events right now</p>
            <p className="text-[#4A4A6A] text-xs mt-1">New events will appear here when they go live.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {gameEvents.map((event) => {
              const Icon = getEventIcon(event.iconName)
              const isLive = event.status === "live"
              const dateLabel = event.startDate
                ? new Date(event.startDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                : event.status === "coming_soon" ? "Coming Soon" : "Ongoing"

              return (
                <div
                  key={event._id}
                  className="p-4 rounded-xl bg-[#222240] border-2 border-[#2A2A45] cursor-pointer hover:border-[#F37020]/30 transition-colors"
                  onClick={() => setSelectedEventId(event._id)}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-4 h-4" style={{ color: event.badgeColor }} />
                    <span
                      className="text-xs font-bold uppercase px-2 py-0.5 rounded-full border border-[#2A2A45]"
                      style={{ color: event.badgeColor, backgroundColor: `${event.badgeColor}18` }}
                    >
                      {event.badge}
                    </span>
                    {isLive && (
                      <span className="ml-auto flex items-center gap-1.5 text-xs font-bold text-red-400">
                        <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                        LIVE
                      </span>
                    )}
                  </div>
                  <p className="font-black text-white">{event.title}</p>
                  <div className="mt-2 flex items-center gap-2 text-sm text-[#9A9ABF]">
                    <Calendar className="w-4 h-4" />
                    <span>{dateLabel}</span>
                  </div>
                  {event.description && (
                    <p className="mt-2 text-sm text-[#CCCCCC]">{event.description.slice(0, 120)}{event.description.length > 120 ? "..." : ""}</p>
                  )}
                  {/* Progress indicator */}
                  {event.progress && event.progress.requirementProgress.length > 0 && (
                    <div className="mt-3 flex items-center gap-2">
                      <div className="flex-1 h-2 bg-[#1A1A30] rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${Math.min(
                              (event.progress.requirementProgress.reduce((sum, r) => sum + r.current, 0) /
                                Math.max(event.progress.requirementProgress.reduce((sum, r) => sum + r.target, 0), 1)) * 100,
                              100
                            )}%`,
                            background: `linear-gradient(90deg, ${event.badgeColor}, #FFD700)`,
                          }}
                        />
                      </div>
                      <span className="text-[10px] font-bold text-[#9A9ABF]">
                        {event.progress.status === "completed" ? "Done!" : "In Progress"}
                      </span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </motion.div>

      {/* Game Event Detail Modal */}
      <GameEventDetailModal
        open={!!selectedEventId}
        onClose={() => setSelectedEventId(null)}
        eventId={selectedEventId}
      />

      {/* Scheduled Sessions Section */}
      <motion.div
        className="bg-[#1C1C32] border-4 border-[#2A2A45] rounded-2xl p-5 md:p-6 shadow-[6px_6px_0_0_#1a1a1a]"
        variants={itemVariants}
      >
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Mic className="w-5 h-5 text-[#F59E0B]" />
            <h2 className="text-xl font-black text-white">Scheduled Sessions</h2>
            <span className="text-xs text-[#6A6A8A] font-medium">(Event schedule)</span>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-[#2A2A45] bg-[#222240] text-[#9A9ABF] text-xs font-bold hover:bg-[#2A2A45] transition-colors disabled:opacity-60"
          >
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="py-8 text-center text-[#6A6A8A] text-sm font-medium inline-flex items-center justify-center gap-2 w-full">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading events...
          </div>
        ) : error ? (
          <div className="py-8 text-center text-red-500 text-sm font-medium">{error}</div>
        ) : nonLiveAmaEvents.length === 0 ? (
          <div className="py-8 text-center">
            <div className="w-14 h-14 rounded-full bg-[#222240] border-2 border-[#2A2A45] flex items-center justify-center mx-auto mb-3">
              <Mic className="w-6 h-6 text-[#6A6A8A]" />
            </div>
            <p className="text-[#6A6A8A] text-sm font-medium">No scheduled sessions yet</p>
            <p className="text-[#4A4A6A] text-xs mt-1">Check back soon for upcoming sessions.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {nonLiveAmaEvents.map((ama) => {
              const embed = buildAmaEmbedConfig(ama.streamUrl, twitchParentHost)
              return (
                <div
                  key={ama._id}
                  className="p-4 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-2 border-[#F59E0B]/30"
                >
                <div className="flex items-start justify-between gap-3">
                  <p className="font-black text-white">{ama.title}</p>
                  <a
                    href={ama.joinUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#F59E0B] text-black border-2 border-[#2A2A45] text-xs font-black whitespace-nowrap hover:translate-y-[-1px] transition-transform"
                  >
                    Join
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#9A9ABF]">
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    {formatWhen(ama.startsAt)}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Users className="w-4 h-4" />
                    {ama.hostName}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    {ama.durationMinutes} min
                  </span>
                </div>

                {ama.description && (
                  <p className="mt-2 text-sm text-[#CCCCCC]">{ama.description}</p>
                )}

                {embed && (
                  <div className="mt-3 overflow-hidden rounded-xl border-2 border-[#2A2A45] bg-[#11111D]">
                    <div className="aspect-video">
                      <iframe
                        src={embed.src}
                        title={`${ama.title} ${embed.title}`}
                        className="w-full h-full"
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                        allowFullScreen
                      />
                    </div>
                    <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-[#9A9ABF] border-t border-[#2A2A45]">
                      Embedded {embed.provider === "youtube" ? "YouTube" : "Twitch"} stream
                    </div>
                  </div>
                )}

                <div className="mt-2 text-[11px] text-[#6A6A8A] font-semibold uppercase tracking-wide">
                  {ama.sendToAll
                    ? "Audience: All agencies"
                    : `Audience: ${ama.agencyName || ama.agencyId || "Targeted agency"}`}
                </div>
                </div>
              )
            })}
          </div>
        )}
      </motion.div>

      {/* Submit a Question Section */}
      <motion.div
        className="mt-6 bg-[#1C1C32] border-4 border-[#2A2A45] rounded-2xl p-5 md:p-6 shadow-[6px_6px_0_0_#1a1a1a]"
        variants={itemVariants}
      >
        <div className="flex items-center gap-2 mb-3">
          <MessageCircle className="w-5 h-5 text-[#F59E0B]" />
          <h2 className="text-xl font-black text-white">Submit a Question</h2>
        </div>
        <p className="text-sm text-[#9A9ABF] mb-3">
          Drop a question for the next live event and we can surface the most upvoted ones.
        </p>
        <div className="flex flex-col md:flex-row gap-3">
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask anything..."
            className="flex-1 px-4 py-3 rounded-xl border-2 border-[#2A2A45] bg-[#1A1A30] text-white text-sm font-medium outline-none focus:ring-2 focus:ring-[#F37020]/50 focus:border-[#F37020]/50 placeholder:text-[#6A6A8A]"
          />
          <button
            type="button"
            onClick={handleQuestionSubmit}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-[#F37020] to-[#FF8C42] text-white border-2 border-[#2A2A45] shadow-[0_0_10px_rgba(243,112,32,0.3)] font-bold text-sm hover:translate-y-[-1px] transition-transform"
          >
            Submit
            <Send className="w-4 h-4" />
          </button>
        </div>
        {questionFeedback && (
          <div className="mt-3 text-xs font-semibold text-emerald-400">
            {questionFeedback}
          </div>
        )}
        <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#6A6A8A]">
          <Clock className="w-3.5 h-3.5" />
          Event announcements are auto-posted to chat by admins.
        </div>
      </motion.div>
    </motion.div>
  )
}
